import { PrayerTimes } from "../types/PrayerTypes";
import { PRAYER_TYPES, PRAYER_TIME_KEYS } from "../constants/prayerConstants";
import {
  timeStrToMinutes,
  getJummahDisplayInfo,
  parseTimeToDate,
  getCurrentTimeInTimezone,
} from "../utils/timeUtils";

export function getNextPrayerTime(
  prayerType: string,
  todayTimes: PrayerTimes,
  tomorrowTimes: PrayerTimes | null,
  currentTimeStr: string,
): { time: string | undefined; iqama: string | undefined; isToday: boolean } {
  // Default values
  let time: string | undefined;
  let iqama: string | undefined;
  let isToday = true;

  // Azure Best Practice: Use our centralized constants for prayer time keys
  const keys = PRAYER_TIME_KEYS[
    prayerType as keyof typeof PRAYER_TIME_KEYS
  ] as {
    BEG?: string;
    JAM?: string;
    BEG_ALT?: string;
    JAM_ALT?: string;
  };

  if (!keys) {
    console.warn(`No key mapping found for prayer type: ${prayerType}`);
    return { time: undefined, iqama: undefined, isToday: true };
  }

  // Get beginning time using primary key, falling back to alternative if missing/empty
  const resolveField = (times: PrayerTimes): string | undefined => {
    if (keys.BEG) {
      const val = times[keys.BEG as keyof PrayerTimes] as string;
      if (val && val !== "--:--") return val;
    }
    if (keys.BEG_ALT) {
      const alt = times[keys.BEG_ALT as keyof PrayerTimes] as string;
      if (alt && alt !== "--:--") return alt;
    }
    return undefined;
  };

  const todayBeg = resolveField(todayTimes);

  // Get jamaah time using primary or alternative key
  const todayJam = keys.JAM
    ? (todayTimes[keys.JAM as keyof PrayerTimes] as string)
    : keys.JAM_ALT
      ? (todayTimes[keys.JAM_ALT as keyof PrayerTimes] as string)
      : undefined;

  // The rest of the logic remains the same
  if (todayJam && currentTimeStr < todayJam) {
    time = todayBeg;
    iqama = todayJam;
    isToday = true;
  } else if (tomorrowTimes) {
    // Get tomorrow's times using same primary/alternate key pattern
    const tomorrowBeg = resolveField(tomorrowTimes);

    const tomorrowJam = keys.JAM
      ? (tomorrowTimes[keys.JAM as keyof PrayerTimes] as string)
      : "JAM_ALT" in keys
        ? (tomorrowTimes[(keys as any).JAM_ALT as keyof PrayerTimes] as string)
        : undefined;

    time = tomorrowBeg;
    iqama = tomorrowJam;
    isToday = false;
  } else {
    time = todayBeg;
    iqama = todayJam;
    isToday = true;
  }

  return { time, iqama, isToday };
}

import { PRAYER_SEQUENCE } from "../constants/prayerConstants";
import { ScreenData } from "../types/ScreenTypes";

/**
 * Azure Best Practice: Enhanced prayer highlighting logic with special handling for sequential Jumu'ah prayers
 */
export function determineNextPrayer(
  todayTimes: PrayerTimes,
  tomorrowTimes: PrayerTimes | null,
  currentTimeStr: string,
  screenData: ScreenData,
): number {
  const prayerTypes = PRAYER_SEQUENCE;
  const currentMinutes = timeStrToMinutes(currentTimeStr);
  const currentDate = new Date();
  const isFriday = currentDate.getDay() === 5;
  const jummahPrayers = ["Jummah1", "Jummah2", "Jummah3", "Jummah4"] as const;

  // Special handling for Friday - check if any Jumu'ah prayer is currently in progress
  if (isFriday) {
    // Check each Jummah prayer to see if any is currently in progress

    for (const jummahKey of jummahPrayers) {
      const jummahTime = todayTimes[jummahKey];
      if (jummahTime && jummahTime !== "--:--") {
        const jummahStartMinutes = timeStrToMinutes(jummahTime);
        const jummahLengthMinutes = Math.ceil(
          getPrayerLength(jummahKey, screenData) / 60,
        );
        const jummahEndMinutes = jummahStartMinutes + jummahLengthMinutes;

        // Check if current time is within this Jummah prayer window
        if (
          currentMinutes >= jummahStartMinutes &&
          currentMinutes < jummahEndMinutes
        ) {
          return 1; // Highlight Dhuhr/Jumu'ah slot when a Jummah prayer is in progress
        }
      }
    }

    // No Jummah prayer is currently in progress, continue with normal logic
  }

  // On Friday the Dhuhr slot is Jumu'ah while any Jumu'ah is still to come, but only once its
  // turn comes after Fajr (checked inside the sequence, so Friday mornings highlight Fajr)
  const jummahToCome =
    isFriday &&
    jummahPrayers.some((jummahKey) => {
      const jummahTime = todayTimes[jummahKey];
      return (
        jummahTime &&
        jummahTime !== "--:--" &&
        timeStrToMinutes(jummahTime) > currentMinutes
      );
    });

  // Standard prayer handling for all other cases
  for (let i = 0; i < prayerTypes.length; i++) {
    if (i === 1 && jummahToCome) return 1;

    const nextPrayer = getNextPrayerTime(
      prayerTypes[i],
      todayTimes,
      tomorrowTimes,
      currentTimeStr,
    );

    const iqamaMinutes = timeStrToMinutes(nextPrayer.iqama || "");

    if (
      nextPrayer.isToday &&
      nextPrayer.iqama &&
      currentMinutes < iqamaMinutes
    ) {
      return i;
    }
  }

  // If all today's prayers have passed, highlight the first prayer of tomorrow
  if (tomorrowTimes) {
    return 0; // Fajr of tomorrow
  }

  // Fallback - highlight the last prayer of today
  return prayerTypes.length - 1;
}

/**
 * Helper: Get prayer length in seconds based on prayer name
 */
export function getPrayerLength(
  prayerName: string,
  screenData: ScreenData,
): number {
  // Map prayer name to the appropriate length setting
  const prayerLengthMap: Record<string, number> = {
    JamFajr: screenData.fajrLength,
    JamDhuhr: screenData.dhuhrLength,
    JamAsar: screenData.asarLength,
    JamMaghrib: screenData.maghribLength,
    JamEsha: screenData.ishaLength,
    Jummah1: screenData.jummahLength,
    Jummah2: 15 * 60, // 15 minutes for J2
    Jummah3: 15 * 60, // 15 minutes for J3
    Jummah4: 15 * 60, // 15 minutes for J4
  };

  const result = prayerLengthMap[prayerName] || 300; // Default to 5 minutes if not found

  return result;
}

/**
 * Returns the index (0..4) of the prayer currently in its Jamaat window, or -1 if none.
 * Friday: Dhuhr slot uses first available Jummah (Jummah1..4).
 */
export function getInProgressPrayerIndex(
  todayTimes: PrayerTimes,
  screenData: ScreenData,
  now: Date = new Date(),
): number {
  const isFriday = now.getDay() === 5;
  const sequence = PRAYER_SEQUENCE;

  // Helper to test a jamaat key/time
  const isWindowActive = (jamaatKey: string | undefined): boolean => {
    if (!jamaatKey) return false;
    const jamStr = (todayTimes as any)[jamaatKey] as string | undefined;
    if (!jamStr) return false;

    const jamDate = parseTimeToDate(jamStr);
    if (!jamDate) return false;

    const lengthSec = getPrayerLength(jamaatKey, screenData); // expects keys like JamFajr, JamDhuhr, Jummah1...
    const end = new Date(jamDate.getTime() + lengthSec * 1000);
    return now >= jamDate && now <= end;
  };

  for (let i = 0; i < sequence.length; i++) {
    const type = sequence[i];

    // Special-case Dhuhr on Friday → use the first JummahN that is in window
    if (isFriday && type === PRAYER_TYPES.DHUHR) {
      const jummahs = ["Jummah1", "Jummah2", "Jummah3", "Jummah4"];
      if (jummahs.some((k) => isWindowActive(k))) return i;
      continue;
    }

    // Resolve jamaat field from PRAYER_TIME_KEYS (JAM or JAM_ALT)
    const keys = PRAYER_TIME_KEYS[type as keyof typeof PRAYER_TIME_KEYS] as
      | { JAM?: string; JAM_ALT?: string }
      | undefined;

    const jamaatKey =
      (keys?.JAM && keys.JAM) ||
      (keys?.JAM_ALT && (keys.JAM_ALT as string)) ||
      undefined;

    if (isWindowActive(jamaatKey)) return i;
  }

  return -1;
}

/**
 * Unified highlight: prefer current-in-window; otherwise fall back to "next prayer".
 */
export function getHighlightedPrayerIndex(
  todayTimes: PrayerTimes,
  tomorrowTimes: PrayerTimes | null,
  screenData: ScreenData,
  currentTimeStr: string,
  now: Date = new Date(),
): number {
  const inProgress = getInProgressPrayerIndex(todayTimes, screenData, now);
  if (inProgress >= 0) return inProgress;
  return determineNextPrayer(
    todayTimes,
    tomorrowTimes,
    currentTimeStr,
    screenData,
  );
}

function getPrayerMinutesByField(
  prayerTimes: PrayerTimes,
  prayerField: string | undefined,
): number | null {
  if (!prayerField) return null;

  const raw = (prayerTimes as unknown as Record<string, string | undefined>)[
    prayerField
  ];
  if (!raw || raw === "--:--") return null;

  const [h, m] = raw.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;

  return h * 60 + m;
}

export function isNightModeActive(
  screenData: ScreenData,
  prayerTimes: PrayerTimes | null | undefined,
  now?: Date,
): boolean {
  if (!screenData.enableNightMode || !prayerTimes) {
    return false;
  }

  // If a screen switching rule with apply-intensity action matched, activate dimming now
  if (
    screenData.routingMatched &&
    screenData.matchedActionType === "apply-intensity"
  ) {
    return true;
  }

  const currentTime = now || getCurrentTimeInTimezone(screenData.timeZone);
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
  const triggerType = screenData.nightModeTriggerType ?? "auto";

  let startMinutesBase: number | null;
  let endMinutesBase: number | null;
  let startPrayerName: string | null = null;

  if (triggerType === "prayer") {
    const startPrayer = screenData.nightModeStartPrayer || "JamMaghrib";
    const endPrayer = screenData.nightModeEndPrayer || "Sunrise";
    startPrayerName = startPrayer;
    startMinutesBase = getPrayerMinutesByField(prayerTimes, startPrayer);
    endMinutesBase = getPrayerMinutesByField(prayerTimes, endPrayer);
  } else {
    startMinutesBase =
      getPrayerMinutesByField(prayerTimes, "BegMaghrib") ??
      getPrayerMinutesByField(prayerTimes, "JamMaghrib");
    endMinutesBase = getPrayerMinutesByField(prayerTimes, "Sunrise");
  }

  if (startMinutesBase === null || endMinutesBase === null) {
    return false;
  }

  // Apply start offset: shift window start later (after prayer ends + delay)
  let startMinutes = startMinutesBase;
  if (startPrayerName && screenData.nightModeSecondsAfterStartPrayerEnds) {
    const prayerLen = getPrayerLength(startPrayerName, screenData);
    const offsetMins = Math.round(
      (prayerLen + screenData.nightModeSecondsAfterStartPrayerEnds) / 60,
    );
    startMinutes = ((startMinutesBase + offsetMins) % 1440 + 1440) % 1440;
  }

  // Apply end offset: shift window end earlier (before end prayer)
  let endMinutes = endMinutesBase;
  if (screenData.nightModeSecondsBeforeEndPrayer) {
    const offsetMins = Math.round(screenData.nightModeSecondsBeforeEndPrayer / 60);
    endMinutes = ((endMinutesBase - offsetMins) % 1440 + 1440) % 1440;
  }

  if (startMinutes === endMinutes) {
    return false;
  }

  if (startMinutes < endMinutes) {
    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  }

  return currentMinutes >= startMinutes || currentMinutes < endMinutes;
}
